import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);

  const verifyToken = useCallback(async (storedToken) => {
    try {
      const response = await fetch(`${API}/admin/verify`, {
        headers: {
          'Authorization': `Bearer ${storedToken}`
        }
      });
      return response.ok;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('adminToken');
      if (storedToken) {
        const isValid = await verifyToken(storedToken);
        if (isValid) {
          setToken(storedToken);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminTokenExpiry');
        }
      }
      setIsLoading(false);
    };
    checkAuth();
  }, [verifyToken]);

  const login = async (password) => {
    try {
      const response = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminTokenExpiry', data.expires_at);
        setToken(data.token);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { success: false, error: 'Invalid password' };
      }
    } catch (error) {
      return { success: false, error: 'Login failed. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API}/admin/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch {
      // Ignore logout errors
    }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminTokenExpiry');
    setToken(null);
    setIsAuthenticated(false);
  };

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  return (
    <AdminContext.Provider value={{
      isAuthenticated,
      isLoading,
      token,
      login,
      logout,
      getAuthHeaders
    }}>
      {children}
    </AdminContext.Provider>
  );
};
